<template>
  <div :style="root_style">
    <div class="text-center">
      <div class="row">
        <div class="col-12" style="font-weight:bold; font-size: 22px; color: #545454; margin-left: 0px">
          {{graph_title}}
        </div>
        {{graph_desc}}
      </div>
      <div style="margin-top: 40px" v-if="!loading && !chart_data">
        <h3>-no data available-</h3>
      </div>
    </div>
    <div :style="wrap_style">
      <GChart
          v-if="!loading && chart_data"
          :class="{ 'db-chart-fill': fill_height }"
          :resizeDebounce="500"
          :type="chart_type"
          :data="chart_data"
          :options="gchart_options"
          :settings="{ packages: ['corechart'] }"
      />
      <div v-if="loading" style="height:150px; margin-top:260px">
        <div class="text-center text-primary">
          <b-spinner class="align-middle"></b-spinner>
          <strong style="margin-left:20px">Loading...</strong>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import {GChart} from 'vue-google-charts'
import datePicker from 'vue-bootstrap-datetimepicker';

export default {

  components: {
    GChart,
    datePicker
  },
  props: {
    chart_legend: {type: Object, required: false},
    chart_data: {type: Array, required: false},
    chart_type: {type: String, required: true},
    loading: {type: Boolean, required: true},
    graph_title: {type: String, required: true},
    graph_desc: {type: String, required: false},
    graph_height: {type: String, required: true},
    view_window: {type: Object, required: false},
    date_format: {type: String, required: true},
    chart_area: {type: Object, required: false, default: null},
    chart_colors: {type: Array, required: false, default: null},
    curved: {type: Boolean, required: false, default: false},
    stacked: {type: Boolean, required: false, default: false},
    // the default negative margins below are tuned to bleed a full-width chart
    // past its card padding; that bleed overflows badly when the chart shares
    // a row with a sibling instead of taking the full row to itself.
    narrow: {type: Boolean, required: false, default: false},
    // stretches to fill its grid/flex cell instead of using a fixed pixel
    // height, so it lines up with a sibling of different chrome (title/padding)
    // without having to hand-tune matching pixel values
    fill_height: {type: Boolean, required: false, default: false},
  },

  computed: {
    is_bar_chart() {
      return this.chart_type === 'BarChart' || this.chart_type === 'ColumnChart'
    },
    // google charts draw text in svg from js options, so css tokens can't
    // reach them; mirror the midnight ink here (light themes stay black)
    chart_ink() {
      return this.$store.state.profile.site_theme === 'midnight' ? '#d7e1ea' : 'black'
    },
    root_style() {
      const base = 'border: solid; border-color: black; border-width: 1px; border-radius: 25px; padding: 10px;'
      if (this.fill_height) return `${base} margin-top: 0; height: 100%; display: flex; flex-direction: column;`
      return `${base} margin-top: 30px;`
    },
    wrap_style() {
      const margin = this.is_bar_chart ? 'margin: 0 10px;' : (this.narrow ? 'margin: 0;' : 'margin-left: -80px; margin-right: -100px;')
      if (this.fill_height) return `${margin} flex: 1; min-height: 0; display: flex;`
      return margin
    },
    gchart_options() {
      const ink = this.chart_ink
      const opts = {
        ...(this.fill_height ? {} : { height: this.graph_height }),
        titleTextStyle: {
          color: ink,
          fontSize: 27,
          bold: false,
          italic: false
        },
        legend: { position: 'top', alignment: 'center', textStyle: { color: ink } },
        backgroundColor: { fill: 'transparent' },
        lineWidth: 2,
        ...(this.curved ? { curveType: 'function' } : {}),
        ...(this.stacked ? { isStacked: true } : {}),
        colors: this.chart_colors || [ink, 'orange', 'green', 'blue', 'red', 'purple', 'grey'],
        hAxis: {
          gridlines: { color: 'transparent' },
          textStyle: { color: ink },
          titleTextStyle: { color: ink, italic: false, fontSize: 18 },
          baselineColor: { color: ink },
          ...(this.is_bar_chart ? {} : { format: this.date_format })
        },
        ...(this.chart_type !== 'BarChart' && this.chart_type !== 'ColumnChart'
          ? { explorer: { actions: ['dragToZoom'], axis: 'horizontal', keepInBounds: true } }
          : {}),
        vAxis: {
          textStyle: { color: ink },
          titleTextStyle: { color: ink, italic: false, fontSize: 18 },
          baselineColor: { color: ink },
          ...(this.chart_type !== 'BarChart' && this.chart_type !== 'ColumnChart'
            ? { viewWindowMode: 'explicit', viewWindow: this.vw_window }
            : {})
        },
        titlePosition: { color: ink },
        crosshair: { color: ink }
      }
      if (this.chart_area) opts.chartArea = this.chart_area
      return opts
    },
  },

  data() {
    return {
      vw_window: null,
    };
  },
  created() {
    this.vw_window = this.view_window
    this.check_range()
  },
  methods: {
    async update_view_window() {
      if (this.vw_window == null)
        this.vw_window = this.view_window
      else
        this.vw_window = null
      this.check_range()
    },
    check_range() {
      if (this.chart_data && this.vw_window) {
        var max = Math.max(...this.chart_data.filter((_, i) => i > 0).map(i => i[1]))
        var min = Math.min(...this.chart_data.filter((_, i) => i > 0).map(i => i[1]))

        if (min < this.vw_window.min) {
          this.vw_window.min = min
        }
        if (max > this.vw_window.max) {
          this.vw_window.max = max
        }
      }
    }
  }
}

</script>

<style scoped>
/* applied to GChart's root element via class attribute inheritance, so the
   chart canvas actually fills the flex:1 wrapper instead of collapsing to 0 */
.db-chart-fill {
  width: 100%;
  height: 100%;
}
</style>

